import { ConfirmationModal } from '@common/modal/confirmationModal/ConfirmationModal'
import { useLogger } from '@omnia/ui/src/lib/hooks/useLogger'
import type { ReplaceableSubcomponent, ShowtimeButtonProps } from '@vista-digital/occ-ui'
import { defaultOccPlaceholderAssets, OccContextComponent } from '@vista-digital/occ-ui'
import { observe, runInAction } from 'mobx'
import type { FunctionComponent, ReactNode } from 'react'
import { lazy, useEffect, useState } from 'react'
import { OccModal } from '@/common/components/occModal/OccModal'
import { cmsDataService } from '@/content/services/cms.data.service'
import { memberService } from '@/content/services/member.service'
import { mobileEmbedService } from '@/content/services/mobileEmbed/mobileEmbed.service'
import { navigationService } from '@/content/services/navigation.service'
import { occService } from '@/content/services/occ.service'
import { LoginStateTracker } from '../loginStateTracker/LoginStateTracker'
import type { OccConfigurationOverridePropsApi } from '../occConfigurationOverride/models'
import { useOmniaOccProps } from './useOmniaOccProps'

const CustomShowtimeButton = lazy(() => import('@/features/browsing/subcomponents/showtimeButton/CustomShowtimeButton'))

export const OccContext: FunctionComponent<{ children?: ReactNode | undefined }> = props => {
    const logger = useLogger('OccContext')
    const data = cmsDataService.get()

    logger.debug('OCC Context initialised with data', data)

    const [occConfigurationOverride, setOccConfigurationOverride] = useState<
        Partial<Omit<OccConfigurationOverridePropsApi, 'vistaCinema'>>
    >({})

    const [occOverrideData, setOccOverrideData] = useState<OccConfigurationOverridePropsApi | undefined>(undefined)

    const disposer = observe(occService.overrideSiteIdObs, ({ oldValue, newValue }) => {
        if (newValue !== oldValue) {
            runInAction(() => {
                const occOverride = cmsDataService.getOccOverride(newValue as string)
                if (occOverride !== occOverrideData) {
                    setOccOverrideData(occOverride)
                    const excludedVistaCinema = { ...occOverride }
                    delete excludedVistaCinema.vistaCinema
                    setOccConfigurationOverride(excludedVistaCinema)
                }
            })
        }
    })

    useEffect(() => {
        return () => disposer()
    }, [])
    const isLivingTicketPage = navigationService && navigationService.getCurrentPage() === 'ReviewTicket'

    // eslint-disable-next-line @typescript-eslint/require-await -- No need to await
    const onTokenExpired = async () => {
        // Prevent reloading the page in Storybook
        if (IS_STORYBOOK) return

        // This 'Token' is the GAS token for the digital-api (aka Connect), NOT the members authentication cookie.
        // Reloading the page will allow us to get a fresh GAS token from the window.initialData object.

        logger.info('GAS token expired, reloading the page!')

        // If GAS token expires while in app embed, close the embed
        if (mobileEmbedService.isMobileApp()) {
            logger.info('GAS token expired while in mobile app, closing the embed.')
            mobileEmbedService.close()
            return
        }

        // eslint-disable-next-line no-restricted-properties -- Refreshing the GAS token not used for navigation
        window.location.reload()
    }

    const { domain, accessibilityText, assets, config, displayText } = useOmniaOccProps(
        data,
        occConfigurationOverride,
        isLivingTicketPage,
        () => memberService.onSignedOut(),
        onTokenExpired
    )

    if (!props?.children) return null

    return (
        <OccContextComponent
            domain={domain}
            config={config}
            displayText={displayText}
            children={
                <>
                    <LoginStateTracker />
                    {props.children}
                </>
            }
            accessibilityText={accessibilityText}
            assets={assets}
            subcomponents={{
                ShowtimeButton: CustomShowtimeButton as ReplaceableSubcomponent<ShowtimeButtonProps>,
                ConfirmationModal: ConfirmationModal,
                Modal: OccModal,
            }}
            placeholderAssets={{
                ...defaultOccPlaceholderAssets,
                memberRewardImage: {
                    name: 'member-reward-image-placeholder',
                    viewBox: '0 0 400 300',
                },
            }}
        />
    )
}

export { OccContext as default }
